import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import axios from 'axios';
import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  ModalCloseButton,
  Box,
  Flex,
  HStack,
  VStack,
  Text,
  Badge,
  Button,
  IconButton,
  Input,
  Select,
  Checkbox,
  Alert,
  AlertIcon,
  AlertDescription,
  Spinner,
  Collapse,
  Tooltip,
  useBreakpointValue,
} from '@chakra-ui/react';
import { RefreshCw, Clock, CheckCircle2, AlertTriangle, Ticket, ChevronRight, Ban } from 'lucide-react';
import { useAppToast } from '../hooks/useAppToast';
import MatkaBoardModal from './MatkaBoardModal';

const base_url = process.env.REACT_APP_API_URL_ADMIN;
const NUMERIC_DIGITS = { single: 1, double: 2, triple: 3 };
const BONUS_OPTIONS = [1, 2, 3, 4, 5, 6, 7, 8, 9, 10];

const isMatkaGame = (game) =>
  String(game?.gameCode || '').toUpperCase() === 'TM' || String(game?.gameType || '').toLowerCase() === 'matka';
// A patti's digits never decrease when 0 counts as 10 (370, 100, 444, 000).
const isValidPanna = (s) => {
  if (!/^\d{3}$/.test(s)) return false;
  const r = [...s].map((c) => (c === '0' ? 10 : Number(c)));
  return r[0] <= r[1] && r[1] <= r[2];
};
const pannaAnk = (s) => [...s].reduce((sum, c) => sum + Number(c), 0) % 10;
const isNoDraw = (value) => String(value ?? '').trim().toUpperCase() === 'NA';

const istTime = (ms) =>
  new Date(ms).toLocaleTimeString('en-IN', { timeZone: 'Asia/Kolkata', hour: '2-digit', minute: '2-digit', hour12: false });
const istDate = (ms) =>
  new Date(ms).toLocaleDateString('en-IN', { timeZone: 'Asia/Kolkata', day: '2-digit', month: 'short' });
const fmtPts = (n) => Number(n || 0).toLocaleString('en-IN');
const plural = (n, word) => `${n} ${word}${n === 1 ? '' : 's'}`;

const apiMessage = (err, fallback) =>
  err?.response?.data?.message || err?.message || fallback;

async function post(body) {
  try {
    const res = await axios.post(`${base_url}`, body);
    const data = res.data || {};
    if (data.status && data.status >= 400) {
      const e = new Error(data.message || 'Request failed');
      e.body = data;
      throw e;
    }
    return data;
  } catch (err) {
    if (err.body) throw err;
    const e = new Error(apiMessage(err, 'Request failed'));
    e.body = err.response?.data;
    throw e;
  }
}

const drawState = (draw, nowMs) => {
  if (draw.result != null) return 'declared';
  if (draw.draw_time_epoc > nowMs) return 'upcoming';
  return 'awaiting';
};

const STATE_META = {
  awaiting: { label: 'Awaiting result', color: 'orange', icon: AlertTriangle },
  upcoming: { label: 'Upcoming', color: 'blue', icon: Clock },
  declared: { label: 'Declared', color: 'green', icon: CheckCircle2 },
};

const scheduledPatti = (scheduled) => String(scheduled?.result || '').split(',').pop();

const ResultForm = ({ draw, digits: gameDigits, matka, initialResult = '', upcoming, startNoDraw = false, onPublished }) => {
  const { showSuccess } = useAppToast();
  const isDeclared = draw.result != null;
  const declaredNoDraw = isDeclared && isNoDraw(draw.result);
  const scheduled = !isDeclared ? draw.scheduled : null;
  const pending = scheduled?.status === 'pending';
  const pendingNoDraw = pending && isNoDraw(scheduled.result);
  const pendingResult = pending && !pendingNoDraw;
  const [result, setResult] = useState(
    initialResult || (pendingResult ? (matka ? scheduledPatti(scheduled) : scheduled.result) : '')
  );
  const [bonus, setBonus] = useState(pendingResult ? scheduled.bonus || 1 : 1);
  const [overwrite, setOverwrite] = useState(false);
  const [confirming, setConfirming] = useState(false);
  const [confirmingNoDraw, setConfirmingNoDraw] = useState(startNoDraw && !isDeclared && !pendingNoDraw);
  const [saving, setSaving] = useState(false);
  const [cancelling, setCancelling] = useState(false);
  const [error, setError] = useState('');

  const digits = matka ? 3 : gameDigits;
  const cleaned = digits ? result.replace(/\D/g, '').slice(0, digits) : result.trim().toUpperCase().slice(0, 10);
  const pattiOk = matka && isValidPanna(cleaned);
  const ank = pattiOk ? pannaAnk(cleaned) : null;
  const finalResult = matka ? (pattiOk ? `${ank},${cleaned}` : '') : cleaned;
  const validationError = !cleaned
    ? matka ? 'Enter the patti' : 'Enter the result'
    : isNoDraw(cleaned)
      ? 'Use the No Draw button to declare NA'
      : digits && cleaned.length !== digits
        ? matka ? 'Patti must be exactly 3 digits' : `Result must be exactly ${digits} digit${digits > 1 ? 's' : ''}`
        : matka && !pattiOk
          ? `${cleaned} is not a patti from the chart (digits must rise, 0 counts as 10)`
          : isDeclared && !overwrite
            ? 'Tick the box to replace the existing result'
            : '';
  const refund = draw.noDrawRefund;

  const publish = async () => {
    setSaving(true);
    setError('');
    try {
      if (upcoming) {
        const res = await post({
          apiName: 'draw',
          command: 'scheduleManualResult',
          drawId: draw.draw_id,
          result: finalResult,
          bonus,
        });
        showSuccess(
          `Result ${res.data?.result || finalResult} (${bonus}x) scheduled · publishes at ${istTime(draw.draw_time_epoc)}`
        );
      } else {
        const res = await post({
          apiName: 'draw',
          command: 'publishManualResult',
          drawId: draw.draw_id,
          result: finalResult,
          bonus,
          overwrite: isDeclared,
        });
        const settled = res.data?.settlement;
        const reversed = res.data?.noDrawReversal;
        showSuccess(
          `Result ${res.data?.result || finalResult} (${bonus}x) published for ${istTime(draw.draw_time_epoc)}` +
            (settled && typeof settled === 'object' && settled.winners !== undefined ? ` · ${settled.winners} winning tickets` : '') +
            (reversed?.restored ? ` · ${fmtPts(reversed.clawedBackPoints)} pts of No Draw refunds taken back` : '')
        );
      }
      onPublished();
    } catch (err) {
      setError(err.message);
      setConfirming(false);
    } finally {
      setSaving(false);
    }
  };

  const declareNoDraw = async () => {
    setSaving(true);
    setError('');
    try {
      const res = await post({ apiName: 'draw', command: 'setNoDraw', drawId: draw.draw_id });
      const settled = res.data?.settlement;
      showSuccess(
        upcoming
          ? `Draw ${istTime(draw.draw_time_epoc)} set to No Draw · booking closed, refunds at draw time`
          : `Draw ${istTime(draw.draw_time_epoc)} declared No Draw · ${plural(settled?.refunded || 0, 'ticket')} refunded`
      );
      onPublished();
    } catch (err) {
      setError(err.message);
      setConfirmingNoDraw(false);
    } finally {
      setSaving(false);
    }
  };

  const cancelSchedule = async () => {
    setCancelling(true);
    setError('');
    try {
      await post({ apiName: 'draw', command: 'cancelScheduledResult', drawId: draw.draw_id });
      showSuccess(
        pendingNoDraw
          ? `No Draw for ${istTime(draw.draw_time_epoc)} undone · booking is open again`
          : `Scheduled result for ${istTime(draw.draw_time_epoc)} cancelled`
      );
      onPublished();
    } catch (err) {
      setError(err.message);
    } finally {
      setCancelling(false);
    }
  };

  const busy = confirming || confirmingNoDraw;

  return (
    <Box mt={3} p={{ base: 3, md: 4 }} bg="white" borderRadius="lg" borderWidth="1px" borderColor="blue.100" onClick={(e) => e.stopPropagation()}>
      {declaredNoDraw ? (
        <Alert status="warning" borderRadius="md" mb={3} py={2} alignItems="flex-start">
          <AlertIcon />
          <AlertDescription fontSize="sm">
            Declared <b>No Draw</b>: {plural(refund?.tickets || 0, 'ticket')} ({fmtPts(refund?.points)} pts) refunded to{' '}
            {plural(refund?.players || 0, 'player')}. Publishing a result takes these refunds back from the players (balances may
            go negative) and settles the tickets normally.
          </AlertDescription>
        </Alert>
      ) : isDeclared && (
        <Alert status="warning" borderRadius="md" mb={3} py={2}>
          <AlertIcon />
          <AlertDescription fontSize="sm">
            Already declared as <b>{draw.result}</b> ({draw.bonus || 1}x). Publishing again replaces it and re-settles all tickets.
          </AlertDescription>
        </Alert>
      )}
      {pending && (
        <Alert status={pendingNoDraw ? 'error' : 'info'} borderRadius="md" mb={3} py={2} flexWrap="wrap" gap={2}>
          <AlertIcon />
          <AlertDescription fontSize="sm" flex="1" minW="200px">
            {pendingNoDraw ? (
              upcoming
                ? <>Set to <b>No Draw</b>: booking is closed. At <b>{istTime(draw.draw_time_epoc)}</b> the result becomes NA and every ticket is refunded.</>
                : <><b>No Draw</b> · refunding now…</>
            ) : (
              <>
                Scheduled <b>{scheduled.result}</b> ({scheduled.bonus}x)
                {upcoming ? <> · publishes automatically at <b>{istTime(draw.draw_time_epoc)}</b>. Save again to change it.</> : ' · publishing now…'}
              </>
            )}
          </AlertDescription>
          {upcoming && (
            <Button size="sm" colorScheme="red" variant="outline" onClick={cancelSchedule} isLoading={cancelling}>
              {pendingNoDraw ? 'Undo No Draw' : 'Cancel schedule'}
            </Button>
          )}
        </Alert>
      )}
      {scheduled?.status === 'failed' && (
        <Alert status="error" borderRadius="md" mb={3} py={2}>
          <AlertIcon />
          <AlertDescription fontSize="sm">
            Scheduled {isNoDraw(scheduled.result) ? 'No Draw' : <>result <b>{scheduled.result}</b></>} could not be published:{' '}
            {scheduled.error || 'unknown error'}. Publish it below.
          </AlertDescription>
        </Alert>
      )}

      {!pendingNoDraw && (
        <Flex gap={{ base: 3, md: 4 }} align="flex-end" wrap="wrap">
          <Box flex={{ base: '1 1 100%', sm: '1' }} minW={{ sm: '160px' }}>
            <Text fontSize="xs" fontWeight="700" color="gray.500" textTransform="uppercase" mb={1}>
              {matka ? 'Patti (3 digits)' : `Result ${digits ? `(${digits} digit${digits > 1 ? 's' : ''})` : ''}`}
            </Text>
            <Input
              autoFocus={!startNoDraw}
              value={digits ? cleaned : result}
              onChange={(e) => { setResult(e.target.value); setConfirming(false); }}
              placeholder={digits ? '0'.repeat(digits) : 'Result'}
              inputMode={digits ? 'numeric' : 'text'}
              size="lg"
              fontFamily="mono"
              fontSize="2xl"
              fontWeight="700"
              letterSpacing="0.3em"
              textAlign="center"
              maxLength={digits || 10}
            />
          </Box>
          {matka && (
            <Box flex={{ base: '1', sm: 'none' }} w={{ sm: '90px' }}>
              <Text fontSize="xs" fontWeight="700" color="gray.500" textTransform="uppercase" mb={1}>Ank</Text>
              <Flex
                h="48px"
                align="center"
                justify="center"
                borderRadius="md"
                borderWidth="1px"
                borderColor={ank != null ? 'green.300' : 'gray.200'}
                bg={ank != null ? 'green.50' : 'gray.50'}
                fontFamily="mono"
                fontSize="2xl"
                fontWeight="800"
                color={ank != null ? 'green.600' : 'gray.300'}
              >
                {ank != null ? ank : '–'}
              </Flex>
            </Box>
          )}
          <Box flex={{ base: '1', sm: 'none' }} w={{ sm: '120px' }}>
            <Text fontSize="xs" fontWeight="700" color="gray.500" textTransform="uppercase" mb={1}>Bonus</Text>
            <Select size="lg" value={bonus} onChange={(e) => { setBonus(Number(e.target.value)); setConfirming(false); }}>
              {BONUS_OPTIONS.map((b) => <option key={b} value={b}>{b}x</option>)}
            </Select>
          </Box>
        </Flex>
      )}

      {isDeclared && (
        <Checkbox mt={3} isChecked={overwrite} onChange={(e) => setOverwrite(e.target.checked)} colorScheme="orange">
          <Text fontSize="sm">Replace the existing result</Text>
        </Checkbox>
      )}

      {error && (
        <Alert status="error" borderRadius="md" mt={3} py={2}>
          <AlertIcon />
          <AlertDescription fontSize="sm">{error}</AlertDescription>
        </Alert>
      )}

      {!busy && !pendingNoDraw && (
        <Flex mt={4} justify="space-between" align="center" gap={3} wrap="wrap">
          <Text fontSize="sm" color={validationError ? 'gray.500' : 'gray.700'} flex="1 1 200px">
            {validationError ||
              (upcoming
                ? `Publishes at ${istTime(draw.draw_time_epoc)} · ${plural(draw.tickets, 'ticket')} booked so far.`
                : `${plural(draw.tickets, 'ticket')} will be settled.`)}
          </Text>
          <HStack spacing={2} flex={{ base: '1 1 100%', sm: 'none' }}>
            {!isDeclared && (
              <Button
                leftIcon={<Ban size={16} />}
                colorScheme="red"
                variant="outline"
                flex={{ base: 1, sm: 'none' }}
                onClick={() => { setConfirmingNoDraw(true); setError(''); }}
              >
                No Draw
              </Button>
            )}
            <Button
              colorScheme="blue"
              flex={{ base: 1, sm: 'none' }}
              isDisabled={Boolean(validationError)}
              onClick={() => setConfirming(true)}
            >
              Continue
            </Button>
          </HStack>
        </Flex>
      )}

      {confirming && (
        <Box mt={4} p={3} borderRadius="md" bg="blue.50" borderWidth="1px" borderColor="blue.200">
          {upcoming ? (
            <Text fontSize="sm" mb={3}>
              Schedule <b style={{ fontFamily: 'monospace', fontSize: '1.1em' }}>{finalResult}</b> ({bonus}x) for draw{' '}
              <b>{draw.draw_id}</b>? Players can&apos;t see it until it is published automatically at{' '}
              <b>{istTime(draw.draw_time_epoc)}</b>, when every ticket booked by then is settled.
            </Text>
          ) : (
            <Text fontSize="sm" mb={3}>
              Publish <b style={{ fontFamily: 'monospace', fontSize: '1.1em' }}>{finalResult}</b> ({bonus}x) for draw{' '}
              <b>{draw.draw_id}</b> at <b>{istTime(draw.draw_time_epoc)}</b>? Winning tickets are paid immediately.
              {declaredNoDraw && refund?.tickets > 0 && (
                <> First, <b>{fmtPts(refund.points)} pts</b> of No Draw refunds are taken back from {plural(refund.players, 'player')}.</>
              )}
            </Text>
          )}
          <HStack justify="flex-end">
            <Button variant="ghost" onClick={() => setConfirming(false)} isDisabled={saving}>Back</Button>
            <Button
              colorScheme="green"
              onClick={publish}
              isLoading={saving}
              loadingText={upcoming ? 'Scheduling' : 'Publishing'}
            >
              {upcoming ? 'Schedule result' : 'Publish result'}
            </Button>
          </HStack>
        </Box>
      )}

      {confirmingNoDraw && (
        <Box mt={4} p={3} borderRadius="md" bg="red.50" borderWidth="1px" borderColor="red.200">
          <Text fontSize="sm" mb={3}>
            {upcoming ? (
              <>
                Set draw <b>{draw.draw_id}</b> ({istTime(draw.draw_time_epoc)}) to <b>No Draw</b>? Booking closes now. At{' '}
                <b>{istTime(draw.draw_time_epoc)}</b> the result becomes <b>NA</b> and every ticket booked by then is refunded
                ({plural(draw.tickets, 'ticket')}, {fmtPts(draw.points)} pts so far).
              </>
            ) : (
              <>
                Declare draw <b>{draw.draw_id}</b> ({istTime(draw.draw_time_epoc)}) as <b>No Draw</b>? The result becomes{' '}
                <b>NA</b> and all {plural(draw.tickets, 'ticket')} ({fmtPts(draw.points)} pts) are refunded to the players now.
              </>
            )}
          </Text>
          <HStack justify="flex-end">
            <Button variant="ghost" onClick={() => setConfirmingNoDraw(false)} isDisabled={saving}>Back</Button>
            <Button
              colorScheme="red"
              leftIcon={<Ban size={16} />}
              onClick={declareNoDraw}
              isLoading={saving}
              loadingText="Saving"
            >
              Confirm No Draw
            </Button>
          </HStack>
        </Box>
      )}
    </Box>
  );
};

const DrawRow = ({ draw, state, nowMs, isOpen, onToggle, digits, matka, initialResult, startNoDraw, onPublished, showDate }) => {
  const declaredNoDraw = state === 'declared' && isNoDraw(draw.result);
  const meta = declaredNoDraw ? { label: 'No Draw', color: 'red', icon: Ban } : STATE_META[state];
  const Icon = meta.icon;
  const minutesLeft = Math.ceil((draw.draw_time_epoc - nowMs) / 60000);
  const scheduled = state !== 'declared' ? draw.scheduled : null;
  const scheduledPending = scheduled?.status === 'pending';
  const scheduledNoDraw = scheduledPending && isNoDraw(scheduled.result);

  return (
    <Box
      borderWidth="1px"
      borderColor={isOpen ? 'blue.300' : 'gray.200'}
      bg={isOpen ? 'blue.50' : state === 'awaiting' ? 'orange.50' : 'white'}
      borderRadius="xl"
      px={{ base: 3, md: 4 }}
      py={3}
      cursor="pointer"
      opacity={state === 'upcoming' && !isOpen ? 0.85 : 1}
      transition="all 0.15s ease"
      _hover={{ borderColor: 'blue.300', shadow: 'sm' }}
      onClick={onToggle}
    >
      <Flex align="center" gap={{ base: 2, md: 4 }}>
        <Box minW={{ base: '58px', md: '78px' }}>
          <Text fontSize={{ base: 'lg', md: 'xl' }} fontWeight="800" fontFamily="mono" lineHeight="1.1">{istTime(draw.draw_time_epoc)}</Text>
          {showDate && <Text fontSize="xs" color="gray.500">{istDate(draw.draw_time_epoc)}</Text>}
        </Box>
        <Box flex="1" minW={0}>
          <Flex gap={2} align="center" wrap="wrap">
            <Badge colorScheme={meta.color} borderRadius="full" px={2} display="inline-flex" alignItems="center" gap={1}>
              <Icon size={12} /> {meta.label}
            </Badge>
            <Text fontSize="xs" color="gray.500">Draw {draw.draw_id}</Text>
          </Flex>
          <HStack spacing={2} mt={1} color="gray.600" fontSize="sm">
            {declaredNoDraw ? (
              <Text>
                {plural(draw.noDrawRefund?.tickets || 0, 'ticket')} refunded · {fmtPts(draw.noDrawRefund?.points)} pts
              </Text>
            ) : (
              <>
                <HStack spacing={1}><Ticket size={14} /><Text>{draw.tickets} tickets</Text></HStack>
                <Text>·</Text>
                <Text>{fmtPts(draw.points)} pts</Text>
              </>
            )}
          </HStack>
        </Box>
        {state === 'declared' && (
          <Box textAlign="right">
            {declaredNoDraw ? (
              <Text fontSize="xl" fontWeight="800" fontFamily="mono" color="red.500" lineHeight="1">NA</Text>
            ) : (
              <>
                <Text fontSize={{ base: 'xl', md: '2xl' }} fontWeight="800" fontFamily="mono" color="green.600" lineHeight="1">{draw.result}</Text>
                <Text fontSize="xs" color="gray.500">{draw.bonus || 1}x</Text>
              </>
            )}
          </Box>
        )}
        {scheduledPending && (
          <Box textAlign="right">
            <Text
              fontSize={scheduledNoDraw ? 'md' : 'xl'}
              fontWeight="800"
              fontFamily={scheduledNoDraw ? undefined : 'mono'}
              color={scheduledNoDraw ? 'red.500' : 'purple.600'}
              lineHeight="1.1"
            >
              {scheduledNoDraw ? 'No Draw' : scheduled.result}
            </Text>
            <Text fontSize="xs" color={scheduledNoDraw ? 'red.400' : 'purple.500'}>
              {state === 'upcoming'
                ? scheduledNoDraw ? 'booking closed' : `scheduled · ${scheduled.bonus}x`
                : 'publishing…'}
            </Text>
          </Box>
        )}
        {scheduled?.status === 'failed' && (
          <Badge colorScheme="red" borderRadius="full" px={2}>Schedule failed</Badge>
        )}
        {state === 'upcoming' && !scheduledPending && (
          <Text fontSize="sm" color="blue.600" fontWeight="600" whiteSpace="nowrap">
            in {minutesLeft >= 60 ? `${Math.floor(minutesLeft / 60)}h ${minutesLeft % 60}m` : `${minutesLeft}m`}
          </Text>
        )}
        <Box color="gray.400" transform={isOpen ? 'rotate(90deg)' : 'none'} transition="transform 0.15s ease">
          <ChevronRight size={20} />
        </Box>
      </Flex>
      <Collapse in={isOpen} animateOpacity unmountOnExit>
        <ResultForm
          key={`${initialResult || ''}|${scheduled?.result || ''}|${state}|${startNoDraw ? 'nd' : ''}`}
          draw={draw}
          digits={digits}
          matka={matka}
          initialResult={initialResult}
          upcoming={state === 'upcoming'}
          startNoDraw={startNoDraw}
          onPublished={onPublished}
        />
      </Collapse>
    </Box>
  );
};

const Section = ({ title, count, children }) => (
  <Box>
    <HStack mb={2} spacing={2}>
      <Text fontSize="xs" fontWeight="800" color="gray.500" textTransform="uppercase" letterSpacing="0.08em">{title}</Text>
      <Badge borderRadius="full" colorScheme="gray">{count}</Badge>
    </HStack>
    <VStack align="stretch" spacing={2}>{children}</VStack>
  </Box>
);

/** Lists a manual-draw game's draws (today + older unresolved) and publishes a result for one of them. */
const ManualResultModal = ({ isOpen, onClose, gameId, gameName }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [openId, setOpenId] = useState(null);
  const [nowMs, setNowMs] = useState(Date.now());
  const [boardDrawId, setBoardDrawId] = useState(null);
  const [prefill, setPrefill] = useState({});
  const [noDrawPrompt, setNoDrawPrompt] = useState(null);
  const isMobile = useBreakpointValue({ base: true, md: false }) ?? false;

  const fetchBoard = useCallback((drawId) => post({ apiName: 'draw', command: 'getManualDrawBoard', drawId }), []);

  const load = useCallback(async () => {
    if (!gameId) return;
    setLoading(true);
    setError('');
    try {
      const res = await post({ apiName: 'draw', command: 'getManualDraws', gameId });
      setData(res.data);
      setNowMs(Date.now());
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [gameId]);

  useEffect(() => {
    if (!isOpen) return undefined;
    setOpenId(null);
    setBoardDrawId(null);
    setPrefill({});
    setNoDrawPrompt(null);
    load();
    const timer = setInterval(() => setNowMs(Date.now()), 15000);
    return () => clearInterval(timer);
  }, [isOpen, load]);

  const game = data?.game;
  const digits = game ? (NUMERIC_DIGITS[game.gameType] && (Number(game.positions) || NUMERIC_DIGITS[game.gameType])) || 0 : 0;
  const matka = isMatkaGame(game);

  const groups = useMemo(() => {
    const out = { awaiting: [], upcoming: [], declared: [] };
    (data?.draws || []).forEach((d) => out[drawState(d, nowMs)].push(d));
    out.declared.reverse();
    return out;
  }, [data, nowMs]);

  // Reload once a scheduled result is due so the row moves to Declared without a manual refresh.
  const lastAutoLoad = useRef(0);
  useEffect(() => {
    if (!isOpen || loading) return;
    const due = (data?.draws || []).some(
      (d) => d.result == null && d.scheduled?.status === 'pending' && d.draw_time_epoc + 5000 <= nowMs
    );
    if (due && nowMs - lastAutoLoad.current > 10000) {
      lastAutoLoad.current = nowMs;
      load();
    }
  }, [isOpen, loading, data, nowMs, load]);

  const todayStart = data?.todayStart || 0;
  const onPublished = () => {
    setOpenId(null);
    setPrefill({});
    setNoDrawPrompt(null);
    load();
  };

  const onBoardContinue = (patti) => {
    const id = boardDrawId;
    setBoardDrawId(null);
    setNoDrawPrompt(null);
    setPrefill((cur) => ({ ...cur, [id]: patti }));
    setOpenId(id);
  };

  const onBoardNoDraw = () => {
    const id = boardDrawId;
    setBoardDrawId(null);
    setNoDrawPrompt(id);
    setOpenId(id);
  };

  const renderRows = (rows, state) =>
    rows.map((d) => (
      <DrawRow
        key={d.draw_id}
        draw={d}
        state={state}
        nowMs={nowMs}
        digits={digits}
        matka={matka}
        initialResult={prefill[d.draw_id]}
        startNoDraw={noDrawPrompt === d.draw_id}
        isOpen={openId === d.draw_id}
        onToggle={() => {
          setNoDrawPrompt(null);
          if (matka) setBoardDrawId(d.draw_id);
          else setOpenId((cur) => (cur === d.draw_id ? null : d.draw_id));
        }}
        onPublished={onPublished}
        showDate={d.draw_time_epoc < todayStart}
      />
    ));

  const total = (data?.draws || []).length;

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      size={isMobile ? 'full' : '2xl'}
      scrollBehavior="inside"
      isCentered={!isMobile}
    >
      <ModalOverlay backdropFilter="blur(2px)" />
      <ModalContent borderRadius={isMobile ? 0 : '2xl'} overflow="hidden">
        <ModalHeader
          bgGradient="linear(135deg, #000000 0%, #001f3f 60%, #003366 100%)"
          color="white"
          pr={24}
          py={{ base: 3, md: 4 }}
        >
          <Text fontSize="lg">Give Result</Text>
          <Text fontSize="sm" fontWeight="400" opacity={0.8} noOfLines={2}>
            {game ? `${game.name} · ${game.gameCode} · Game ${game.gameNumber}` : gameName || ''}
            {' · '}
            {new Date(nowMs).toLocaleDateString('en-IN', { timeZone: 'Asia/Kolkata', weekday: 'short', day: '2-digit', month: 'short' })}
          </Text>
        </ModalHeader>
        <Tooltip label="Refresh" placement="left">
          <IconButton
            aria-label="Refresh"
            icon={<RefreshCw size={16} />}
            size="sm"
            variant="ghost"
            color="white"
            _hover={{ bg: 'whiteAlpha.300' }}
            position="absolute"
            top={3}
            right={12}
            onClick={load}
            isLoading={loading && Boolean(data)}
          />
        </Tooltip>
        <ModalCloseButton color="white" top={3} />
        <ModalBody py={{ base: 3, md: 5 }} px={{ base: 3, md: 6 }} bg="gray.50">
          {error && (
            <Alert status="error" borderRadius="md" mb={4}>
              <AlertIcon />
              <AlertDescription>{error}</AlertDescription>
            </Alert>
          )}
          {loading && !data ? (
            <Flex justify="center" py={12}><Spinner size="lg" color="blue.400" /></Flex>
          ) : total === 0 ? (
            <VStack py={10} spacing={2} color="gray.500">
              <Clock size={32} />
              <Text fontWeight="600">No draws for today</Text>
              <Text fontSize="sm" textAlign="center" maxW="360px">
                Draws are created at midnight from the schedule. Turn on &quot;Generate today&apos;s draws&quot; and save the
                game to create a draw for every time in today&apos;s schedule now.
              </Text>
            </VStack>
          ) : (
            <VStack align="stretch" spacing={{ base: 4, md: 5 }}>
              <HStack spacing={{ base: 2, md: 3 }}>
                {['awaiting', 'upcoming', 'declared'].map((key) => (
                  <Box key={key} flex="1" bg="white" borderRadius="lg" borderWidth="1px" p={{ base: 2, md: 3 }} textAlign="center">
                    <Text fontSize={{ base: 'xl', md: '2xl' }} fontWeight="800" color={`${STATE_META[key].color}.500`}>{groups[key].length}</Text>
                    <Text fontSize={{ base: '2xs', md: 'xs' }} color="gray.500" textTransform="uppercase" fontWeight="700">{STATE_META[key].label}</Text>
                  </Box>
                ))}
              </HStack>
              {groups.awaiting.length > 0 && (
                <Section title="Awaiting result" count={groups.awaiting.length}>{renderRows(groups.awaiting, 'awaiting')}</Section>
              )}
              {groups.upcoming.length > 0 && (
                <Section title="Upcoming" count={groups.upcoming.length}>{renderRows(groups.upcoming, 'upcoming')}</Section>
              )}
              {groups.declared.length > 0 && (
                <Section title="Declared" count={groups.declared.length}>{renderRows(groups.declared, 'declared')}</Section>
              )}
            </VStack>
          )}
        </ModalBody>
        <ModalFooter bg="gray.50" borderTopWidth="1px" gap={3} py={{ base: 2, md: 4 }}>
          <Text fontSize="xs" color="gray.500" mr="auto" display={{ base: 'none', sm: 'block' }}>
            Results and No Draw for upcoming draws are applied automatically at the draw time. Times are IST.
          </Text>
          <Button variant="ghost" onClick={onClose} w={{ base: 'full', sm: 'auto' }}>Close</Button>
        </ModalFooter>
      </ModalContent>
      <MatkaBoardModal
        isOpen={Boolean(boardDrawId)}
        onClose={() => setBoardDrawId(null)}
        drawId={boardDrawId}
        fetchBoard={fetchBoard}
        onContinue={onBoardContinue}
        onNoDraw={onBoardNoDraw}
      />
    </Modal>
  );
};

export default ManualResultModal;
